/* eslint-disable max-lines */
import React, { FC, useMemo } from 'react';
import { NavLink as RouterLink, useLocation } from 'react-router-dom';

import { useTranslation } from '@modernloop/i18n';
import { MaybeTooltip } from '@modernloop/shared/components';
import { useFlag } from '@modernloop/shared/feature-flag';
import {
  AllSchedulingTasksIcon,
  CandidateFilledIcon,
  CandidateIcon,
  ChatBubbleFilledIcon,
  DirectoryFilledIcon,
  DirectoryIcon,
  HelpIcon,
  JobFilledIcon,
  JobIcon,
  MentorIcon,
  MenuIcon,
  MetricsFilledIcon,
  MetricsIcon,
  MyTasksIcon,
  ScheduleFilledIcon,
  ScheduleIcon,
  SearchIcon,
  SettingsFilledIcon,
  SettingsIcon,
} from '@modernloop/shared/icons';
import {
  Box,
  Button,
  Divider,
  IconButton,
  ListItemButton,
  ListItemIcon,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';

import HelpCenterUrl from 'src/constants/HelpCenterUrl';
import { Routes } from 'src/constants/routes';

import { useSidebarState } from 'src/contexts/SidebarStateProvider';

import useAuth from 'src/hooks/useAuth';
import useIsNewOrgExperience from 'src/hooks/useIsNewOrgExperience';

import { OrganizationSettingsTab, getOrganizationSettingUrls } from 'src/urls/getOrganizationSettingUrls';

import DevToolsNavButton from 'src/views-new/DevTools/DevToolsNavButton';
import NotificationInbox from 'src/views-new/NotificationInbox';
import { ShowOmniSearchAction, useOmniSearchDispatch } from 'src/views-new/OmniSearch/OmniSearchProvider';

import AccountMenu from './AccountMenu';

type LinkItem = {
  to: string;
  navKey: string;
  tooltip: string;
  text: string;
  icon: React.ReactNode;
  filledIcon?: React.ReactNode;
  newTab?: boolean;
};

const useSxProps = () => {
  const theme = useTheme();
  return useMemo(
    () => ({
      root: {
        height: '100%',
        width: '100%',
        overflow: 'hidden',
        backgroundColor: theme.palette.background.default,
      },
      menuIconExpanded: {
        marginLeft: '6px',
      },
      nav: {
        height: '100%',
        transition: 'width 0.1s ease-in-out',
        backgroundColor: theme.palette.background.header,
      },
      navExpanded: {
        transition: 'width 0.1s ease-in-out',
        width: '200px',
        '& > div': {
          width: '100%',
        },
      },
      routerLink: {
        textDecoration: 'none',
      },
      iconButton: {
        height: '28px',
        width: '28px',
        padding: theme.spacing(1),
        borderRadius: '6px',
        border: 'none',
        color: theme.palette.text.primary,
        '&:hover': {
          backgroundColor: theme.palette.background.contrast,
          boxShadow: 'none',
        },
      },
      iconButtonActive: {
        border: 'none',
        color: theme.palette.getContrastText(theme.palette.primary.main),
        backgroundColor: `${theme.palette.primary.main} !important`,
        '&:hover': {
          boxShadow: theme.shadows[1],
        },
      },
      button: {
        border: 'none',
        display: 'flex',
        justifyContent: 'flex-start',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        borderRadius: '6px',
      },
      buttonActive: {
        border: 'none',
        color: theme.palette.getContrastText(theme.palette.primary.main),
        backgroundColor: `${theme.palette.primary.main} !important`,
        '&:hover': {
          boxShadow: theme.shadows[1],
        },
      },
    }),
    [theme]
  );
};

export type LinkItemDefinition = {
  to: string;
  navKey: string;
  tooltipKey: string;
  textKey: string;
  icon: React.ReactNode;
  filledIcon?: React.ReactNode;
  newTab?: boolean;
};

export const LINK_DEFINITIONS: LinkItemDefinition[] = [
  {
    to: Routes.MySchedulingTasks,
    navKey: 'my-tasks',
    tooltipKey: 'hiring-dashboard-nav:nav.myTasks',
    textKey: 'hiring-dashboard-nav:nav.myTasks',
    icon: <MyTasksIcon />,
    filledIcon: <MyTasksIcon />,
  },
  {
    to: Routes.AllSchedulingTasks,
    navKey: 'all-tasks',
    tooltipKey: 'hiring-dashboard-nav:nav.allTasks',
    textKey: 'hiring-dashboard-nav:nav.allTasks',
    icon: <AllSchedulingTasksIcon />,
    filledIcon: <AllSchedulingTasksIcon />,
  },
  {
    to: Routes.UpcomingEvents,
    navKey: 'upcoming-events',
    tooltipKey: 'hiring-dashboard-nav:nav.upcomingEvents',
    textKey: 'hiring-dashboard-nav:nav.upcomingEvents',
    icon: <ScheduleIcon />,
    filledIcon: <ScheduleFilledIcon />,
  },
  {
    to: Routes.Candidates,
    navKey: 'candidates',
    tooltipKey: 'hiring-dashboard-nav:nav.candidates',
    textKey: 'hiring-dashboard-nav:nav.candidates',
    icon: <CandidateIcon />,
    filledIcon: <CandidateFilledIcon />,
  },
  {
    to: Routes.Jobs,
    navKey: 'jobs',
    tooltipKey: 'hiring-dashboard-nav:nav.jobs',
    textKey: 'hiring-dashboard-nav:nav.jobs',
    icon: <JobIcon />,
    filledIcon: <JobFilledIcon />,
  },
  {
    to: Routes.Interviews,
    navKey: 'modules',
    tooltipKey: 'hiring-dashboard-nav:nav.modules',
    textKey: 'hiring-dashboard-nav:nav.modules',
    icon: <MentorIcon />,
  },
  {
    to: Routes.Employees,
    navKey: 'directory',
    tooltipKey: 'hiring-dashboard-nav:nav.directory',
    textKey: 'hiring-dashboard-nav:nav.directory',
    icon: <DirectoryIcon />,
    filledIcon: <DirectoryFilledIcon />,
  },
  {
    to: Routes.Metrics,
    navKey: 'metrics',
    tooltipKey: 'hiring-dashboard-nav:nav.metrics',
    textKey: 'hiring-dashboard-nav:nav.metrics',
    icon: <MetricsIcon />,
    filledIcon: <MetricsFilledIcon />,
  },
];

const AppNav: FC = () => {
  const { t } = useTranslation();
  const sxProps = useSxProps();
  const auth = useAuth();
  const omniSearchDispatch = useOmniSearchDispatch();
  const { expanded, setExpanded } = useSidebarState();
  const { pathname } = useLocation();
  const isNewOrgExperience = useIsNewOrgExperience();
  const isExpanded = expanded;
  const globalAgentEnabled = useFlag('AI_GLOBAL_AGENT');
  const notificationsInAppBellEnabled = useFlag('NOTIFICATIONS_IN_APP_BELL');

  const LINKS: LinkItem[] = useMemo(
    () =>
      LINK_DEFINITIONS.map((def) => ({
        to: def.to,
        navKey: def.navKey,
        tooltip: t(def.tooltipKey),
        text: t(def.textKey),
        icon: def.icon,
        filledIcon: def.filledIcon,
        newTab: def.newTab,
      })),
    [t]
  );

  const SETTINGS_LINK: LinkItem = useMemo(
    () => ({
      to: getOrganizationSettingUrls({
        tab: OrganizationSettingsTab.SCHEDULE_SETTINGS,
        returnTo: pathname,
      }),
      navKey: 'organization-settings',
      tooltip: t('hiring-dashboard-nav:nav.settings.title'),
      text: t('hiring-dashboard-nav:nav.settings.title'),
      icon: <SettingsIcon />,
      filledIcon: <SettingsFilledIcon />,
    }),
    [pathname, t]
  );

  const HELP_LINK: LinkItem = useMemo(
    () => ({
      to: HelpCenterUrl.HOME,
      navKey: 'help-and-feedback',
      tooltip: t('hiring-dashboard-nav:nav.helpAndFeedback'),
      text: t('hiring-dashboard-nav:nav.helpAndFeedback'),
      icon: <HelpIcon />,
      newTab: true,
    }),
    [t]
  );

  const handleShowOmniSearch = () => {
    omniSearchDispatch(ShowOmniSearchAction);
  };

  function getIcon(icon: React.ReactNode, filledIcon: React.ReactNode | undefined, isActive: boolean) {
    if (isActive && filledIcon) {
      return filledIcon;
    }

    return icon;
  }

  const searchLabel = t('hiring-dashboard-nav:nav.dashboard.search');
  const searchButton = isExpanded ? (
    <Button
      id="app-nav-search"
      data-testid="app-nav-search"
      sx={{ justifyContent: 'flex-start' }}
      size="small"
      fullWidth
      startIcon={<SearchIcon />}
      onClick={handleShowOmniSearch}
    >
      {searchLabel}
    </Button>
  ) : (
    <Tooltip title={searchLabel} placement="right">
      <IconButton
        id="app-nav-search"
        data-testid="app-nav-search"
        sx={sxProps.iconButton}
        onClick={handleShowOmniSearch}
      >
        <SearchIcon />
      </IconButton>
    </Tooltip>
  );

  const renderLink = (link: LinkItem) => {
    const isActive = pathname.startsWith(link.to);
    const navTestId = `app-nav-${link.navKey}`;
    if (isExpanded) {
      const button = (
        <Button
          size="small"
          fullWidth
          variant="text"
          sx={[sxProps.button, isActive ? sxProps.buttonActive : {}]}
          color={isActive ? 'info' : undefined}
          startIcon={getIcon(link.icon, link.filledIcon, isActive)}
        >
          <Typography color={isActive ? 'background' : 'text.primary'} variant="body2" fontWeight={500}>
            {link.text}
          </Typography>
        </Button>
      );

      if (link.newTab) {
        return (
          <a
            key={link.navKey}
            id={navTestId}
            data-testid={navTestId}
            style={sxProps.routerLink}
            href={link.to}
            rel="noopener noreferrer"
            target="_blank"
            tabIndex={-1}
          >
            {button}
          </a>
        );
      }

      return (
        <RouterLink
          key={link.navKey}
          id={navTestId}
          data-testid={navTestId}
          to={link.to}
          style={sxProps.routerLink}
          tabIndex={-1}
        >
          {button}
        </RouterLink>
      );
    }

    const button = (
      <IconButton sx={[sxProps.iconButton, isActive ? sxProps.iconButtonActive : {}]}>
        {getIcon(link.icon, link.filledIcon, isActive)}
      </IconButton>
    );

    if (link.newTab) {
      return (
        <Tooltip title={link.tooltip} placement="right" key={link.navKey}>
          <a
            id={navTestId}
            data-testid={navTestId}
            style={sxProps.routerLink}
            href={link.to}
            rel="noopener noreferrer"
            target="_blank"
            tabIndex={-1}
          >
            {button}
          </a>
        </Tooltip>
      );
    }

    return (
      <Tooltip title={link.tooltip} placement="right" key={link.navKey}>
        <RouterLink id={navTestId} data-testid={navTestId} to={link.to} style={sxProps.routerLink} tabIndex={-1}>
          {button}
        </RouterLink>
      </Tooltip>
    );
  };

  const renderAgentLink = () => {
    if (!globalAgentEnabled) {
      return null;
    }

    if (!isExpanded) {
      return (
        <Tooltip title={t('hiring-dashboard-nav:nav.assistant')} placement="right">
          <IconButton
            id="app-nav-ai-assistant"
            data-testid="app-nav-ai-assistant"
            component={RouterLink}
            to={Routes.Agent}
            sx={sxProps.iconButton}
          >
            <ChatBubbleFilledIcon color="action" />
          </IconButton>
        </Tooltip>
      );
    }

    return (
      <ListItemButton
        id="app-nav-ai-assistant"
        data-testid="app-nav-ai-assistant"
        component={RouterLink}
        to={Routes.Agent}
        sx={{ borderRadius: '6px' }}
      >
        <ListItemIcon>
          <ChatBubbleFilledIcon color="action" />
        </ListItemIcon>
        <Typography color="text.primary" fontWeight="500" variant="body2">
          {t('hiring-dashboard-nav:nav.assistant')}
        </Typography>
      </ListItemButton>
    );
  };

  return (
    <Stack
      id="app-nav"
      data-testid="app-nav"
      paddingTop={isExpanded ? 0 : '9px'}
      sx={[sxProps.nav, isExpanded ? sxProps.navExpanded : {}]}
    >
      {isExpanded && (
        <Stack height="46px" padding="0px 12px 0px 16px" alignItems="center" direction="row" gap={0.5}>
          <Tooltip title={t('hiring-dashboard-nav:nav.collapse')} placement="right">
            <IconButton
              id="app-nav-sidebar-collapse"
              data-testid="app-nav-sidebar-collapse"
              sx={sxProps.iconButton}
              onClick={() => {
                setExpanded(false);
              }}
            >
              <MenuIcon />
            </IconButton>
          </Tooltip>
          <Box
            id="app-nav-org-name"
            data-testid="app-nav-org-name"
            sx={{
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
            }}
          >
            <MaybeTooltip
              label={auth?.userInfo?.orgName ?? ''}
              title={auth?.userInfo?.orgName ?? ''}
              labelProps={{ fontWeight: 600 }}
              placement="right"
            />
          </Box>
        </Stack>
      )}

      {!isNewOrgExperience && isExpanded ? (
        <Stack data-testid="app-nav-search-section" padding="4px 12px">
          {searchButton}
        </Stack>
      ) : null}

      {!isNewOrgExperience && isExpanded ? (
        <Stack data-testid="app-nav-inbox-section" gap="4px" padding="4px 12px 8px">
          {notificationsInAppBellEnabled && <NotificationInbox expanded />}
          {renderAgentLink()}
        </Stack>
      ) : null}

      {!isExpanded && (
        <Stack padding="0px 12px 8px" gap="4px" alignItems="center">
          <Tooltip title={t('hiring-dashboard-nav:nav.expand')} placement="right">
            <IconButton
              id="app-nav-sidebar-expand"
              data-testid="app-nav-sidebar-expand"
              sx={sxProps.iconButton}
              onClick={() => {
                setExpanded(true);
              }}
            >
              <MenuIcon fontSize="medium" />
            </IconButton>
          </Tooltip>
          {/* Only show links if the directory is connected. Otherwise, there is no employee id. */}
          {!isNewOrgExperience ? searchButton : null}
          {!isNewOrgExperience ? (
            <Stack gap="4px" marginTop={1}>
              {notificationsInAppBellEnabled ? <NotificationInbox /> : null}
              {renderAgentLink()}
            </Stack>
          ) : null}
        </Stack>
      )}
      {!isNewOrgExperience && <Divider data-testid="app-nav-primary-divider" />}

      {!isNewOrgExperience && (
        <>
          <Stack
            data-testid="app-nav-tasks-section"
            gap="4px"
            padding="8px 12px"
            alignItems={!isExpanded ? 'center' : undefined}
          >
            {LINKS.slice(0, 3).map((link) => renderLink(link))}
          </Stack>
          <Divider data-testid="app-nav-tasks-divider" />
          <Stack
            data-testid="app-nav-hiring-section"
            gap="4px"
            padding="8px 12px"
            alignItems={!isExpanded ? 'center' : undefined}
          >
            {LINKS.slice(3, 7).map((link) => renderLink(link))}
          </Stack>
          <Divider data-testid="app-nav-hiring-divider" />
          <Stack
            data-testid="app-nav-insights-section"
            gap="4px"
            padding="8px 12px"
            alignItems={!isExpanded ? 'center' : undefined}
          >
            {renderLink(LINKS[7])}
            {renderLink(SETTINGS_LINK)}
          </Stack>
        </>
      )}

      <Stack marginTop="auto">
        <Stack
          data-testid="app-nav-tools-section"
          gap="4px"
          padding="8px 12px"
          alignItems={!isExpanded ? 'center' : undefined}
        >
          {isNewOrgExperience && notificationsInAppBellEnabled && <NotificationInbox expanded={isExpanded} />}
          {/* Keep Dev Tools in the navigation because E2E tests use its stable selector. */}
          <DevToolsNavButton expanded={isExpanded} />
          {renderLink(HELP_LINK)}
        </Stack>
        <Divider data-testid="app-nav-account-divider" />
        <Stack
          data-testid="app-nav-account-section"
          gap="4px"
          padding="8px 12px"
          alignItems={!isExpanded ? 'center' : undefined}
        >
          {/** Only show the account menu if the directory is connected. Otherwise, there is no employee id. */}
          <AccountMenu expanded={isExpanded} size="small" />
        </Stack>
      </Stack>
    </Stack>
  );
};

export default AppNav;
